/**
 * SpherePropertyChangedContent - Renders content for SpherePropertyChanged timeline events
 * Shows specific messages for name/description changes, generic for others
 */

import React from 'react';
import { useTranslation } from 'localization';
import { TimelineEvent } from 'types/timeline';

interface SpherePropertyChangedContentProps {
  event: TimelineEvent;
}

interface PropertyChange {
  old: unknown;
  new: unknown;
}

const SpherePropertyChangedContent: React.FC<SpherePropertyChangedContentProps> = ({ event }) => {
  const { t } = useTranslation();

  const changedProperties: Record<string, PropertyChange> | undefined =
    event.payload?.changedProperties;

  if (!changedProperties || Object.keys(changedProperties).length === 0) {
    return (
      <div className="sphere-timeline-event__content-block">
        <p className="sphere-timeline-event__description">
          {t('activityFeed.events.propertyChanged.generic', {
            actor: event.actor,
            property: t('common.unknown'),
          })}
        </p>
      </div>
    );
  }

  const renderPropertyChange = (key: string, change: PropertyChange): React.ReactNode => {
    switch (key) {
      case 'name':
        return (
          <p key={key} className="sphere-timeline-event__description">
            {t('activityFeed.events.propertyChanged.name', {
              actor: event.actor,
              newName: String(change.new ?? ''),
            })}
          </p>
        );
      case 'description':
        return (
          <p key={key} className="sphere-timeline-event__description">
            {t('activityFeed.events.propertyChanged.description', {
              actor: event.actor,
            })}
          </p>
        );
      default:
        return (
          <p key={key} className="sphere-timeline-event__description">
            {t('activityFeed.events.propertyChanged.generic', {
              actor: event.actor,
              property: key,
            })}
          </p>
        );
    }
  };

  return (
    <div className="sphere-timeline-event__content-block">
      {Object.entries(changedProperties).map(([key, change]) =>
        renderPropertyChange(key, change)
      )}
    </div>
  );
};

export default SpherePropertyChangedContent;
